import React, { useCallback, useContext, useState } from 'react'
import { Box, Dialog, DialogContent, IconButton, useMediaQuery, useTheme } from '@mui/material'
import { Close } from '@mui/icons-material'
import { axiosInstanceMain } from '../../utils/axiosGlobalUtils'
import { TenantsContext } from '../../context/tenants.context'
import SnackBarUtils from '../../utils/SnackBarUtils'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop'
import DownloadIcon from '@mui/icons-material/Download'

const QR: React.FC<{
  codLocal: string
}> = ({ codLocal }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [openQR, setOpenQR] = useState<boolean>(false)
  const [qrImage, setQrImage] = useState<string>(null)
  const { tenant } = useContext(TenantsContext)

  const handleClose = () => {
    setOpenQR(false)
    setQrImage(null)
  }

  const fetchQR = useCallback(async () => {
    try {
      const response = await axiosInstanceMain.get(`/${tenant.path}/locales/${codLocal}/qr`, {
        responseType: 'blob'
      })
      const imageUrl = URL.createObjectURL(response.data)
      setQrImage(imageUrl)
      setOpenQR(true)
    } catch (e) {
      console.log(e)
    }
  }, [codLocal, tenant.path])

  const handleDownload = async () => {
    try {
      const response = await fetch(qrImage)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `QR-${codLocal}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      SnackBarUtils.success('Código QR descargado')
    } catch (error) {
      console.error('Error al descargar:', error)
      SnackBarUtils.error('Error al descargar el código QR')
    }
  }

  const handleCopy = async () => {
    try {
      // Convertir la imagen a PNG usando canvas
      const response = await fetch(qrImage)
      const blob = await response.blob()

      // Crear un canvas para convertir a PNG
      const img = new Image()
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      img.onload = async () => {
        canvas.width = img.width
        canvas.height = img.height
        ctx.drawImage(img, 0, 0)

        // Convertir canvas a blob PNG
        canvas.toBlob(async (pngBlob) => {
          try {
            const clipboardItem = new ClipboardItem({ 'image/png': pngBlob })
            await navigator.clipboard.write([clipboardItem])

            SnackBarUtils.success('Código QR copiado al portapapeles')
          } catch (error) {
            console.error('Error al copiar:', error)

            const msj = String(error).includes('focused')
              ? 'Reinicia la página para copiar el código QR'
              : 'Error al imprimir el código QR'

            SnackBarUtils.error(msj)
          }
        }, 'image/png')
      }

      img.src = qrImage
    } catch (error) {
      console.error('Error al copiar:', error)
    }
  }

  const handlePrint = () => {
    try {
      // Crear una ventana de impresión
      const printWindow = window.open('', '_blank')

      if (printWindow) {
        printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Código QR</title>
            <style>
              body {
                margin: 0;
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
              }
              img {
                max-width: 100%;
                height: auto;
              }
              @media print {
                body {
                  margin: 0;
                }
                img {
                  max-width: 100%;
                  page-break-inside: avoid;
                }
              }
            </style>
          </head>
          <body>
            <img src="${qrImage}" alt="Código QR" />
          </body>
        </html>
      `)

        printWindow.document.close()
        const isChromiumBased = /Chrome|Edg|OPR/.test(navigator.userAgent) && !/Firefox/.test(navigator.userAgent)

        if (isChromiumBased) {
          // Mostrar aviso solo en navegadores Chromium
          SnackBarUtils.info('Cerrá la ventana de impresión para continuar usando la página')
        }
        // Esperar a que la imagen se cargue antes de abrir el diálogo de impresión
        const img = printWindow.document.querySelector('img')
        img.onload = () => {
          printWindow.focus()
          printWindow.print()

          // Manejar el cierre del diálogo de impresión
          printWindow.onafterprint = () => {
            printWindow.close()
            // Devolver el foco a la ventana principal
            setTimeout(() => {
              window.focus()
            }, 100)
          }

          // Fallback en caso de que el usuario cierre la ventana sin imprimir
          const checkClosed = setInterval(() => {
            if (printWindow.closed) {
              clearInterval(checkClosed)
              window.focus()
            }
          }, 500)
        }
      }
    } catch (error) {
      console.error('Error al imprimir:', error)
      SnackBarUtils.error('Error al imprimir el código QR')
    }
  }

  if (!codLocal) return null

  return (
    <>
      <button className="btn btn-secondary" onClick={fetchQR}>
        QR
      </button>
      {qrImage && (
        <Dialog
          open={openQR}
          onClose={handleClose}
          PaperProps={{
            sx: {
              borderRadius: '40px',
              ...(isMobile && {
                marginLeft: '-1rem',
                maxWidth: '80%'
              })
            }
          }}
        >
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
              position: 'absolute',
              right: 2,
              top: 2,
              zIndex: 1
            }}
          >
            <Close />
          </IconButton>
          <DialogContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: '1rem'
              }}
            >
              <img
                src={qrImage}
                alt="Código QR"
                style={{
                  width: isMobile ? '300px' : '350px',
                  height: isMobile ? '300px' : '350px',
                  objectFit: 'contain'
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', width: '100%', gap: '0.7rem' }}>
              <IconButton
                onClick={handlePrint}
                sx={{
                  borderRadius: '50px',
                  border: '1px solid',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  flex: 1,
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white'
                  }
                }}
              >
                <LocalPrintshopIcon sx={{ fontSize: isMobile ? '1.5rem' : '1.7rem' }} />
              </IconButton>
              <IconButton
                onClick={handleDownload}
                sx={{
                  borderRadius: '50px',
                  border: '1px solid',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  flex: 1,
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white'
                  }
                }}
              >
                <DownloadIcon sx={{ fontSize: isMobile ? '1.5rem' : '1.7rem' }} />
              </IconButton>
              <IconButton
                onClick={handleCopy}
                sx={{
                  borderRadius: '50px',
                  border: '1px solid',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  flex: 1,
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white'
                  }
                }}
              >
                <ContentCopyIcon sx={{ fontSize: isMobile ? '1.5rem' : '1.7rem' }} />
              </IconButton>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

export default QR
